import { useRegisterSW } from "virtual:pwa-register/react"
import { Button } from "@nimit9/signet-ui/components/button"
import { useEffect, useRef } from "react"
import { wireAutoApply, wireUpdateChecks } from "@/lib/pwa-update"
import { useCryptoStore } from "@/store/crypto"

/**
 * Toast shown when a new version is waiting, plus automatic updating: the app
 * checks for a new deploy on returning to the foreground and every 30 min, and
 * applies a waiting version by itself the next time it is in the background
 * (see lib/pwa-update.ts). "Reload" applies it straight away.
 */
export function PwaUpdatePrompt() {
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      if (registration) stopChecks.current = wireUpdateChecks(registration)
    },
    onRegisterError(error) {
      console.error("SW registration error", error)
    },
  })

  const stopChecks = useRef<(() => void) | null>(null)
  useEffect(() => () => stopChecks.current?.(), [])

  useEffect(() => {
    if (!needRefresh) return
    return wireAutoApply(
      // A reload clears the in-memory private-mode key, so don't auto-apply
      // while private mode is unlocked; it applies once the app is locked again.
      () => {
        const { mode, key } = useCryptoStore.getState()
        return !(mode === "private" && key)
      },
      () => updateServiceWorker(true),
    )
  }, [needRefresh, updateServiceWorker])

  if (!needRefresh) return null

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed right-4 bottom-4 z-50 flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-3 text-sm text-card-foreground shadow-lg"
    >
      <span>A new version of Paisa is available.</span>
      <Button size="sm" onClick={() => updateServiceWorker(true)}>
        Reload
      </Button>
      <Button size="sm" variant="ghost" onClick={() => setNeedRefresh(false)}>
        Later
      </Button>
    </div>
  )
}
